import { useCallback, useState, useRef } from 'react';

import { useSort } from './useSort';
import { useFetch } from './useFetch';
import { useUrlParam } from './useUrlParam';
import { usePaginated } from './usePaginated';
import { useUpdateEffect } from './useUpdateEffect';
import { useDebouncedValue } from './useDebouncedValue';
import type { Pagination, UsePaginatedResult } from './usePaginated';

const DEFAULT_PAGE_SIZE = 1_000;

export type UseListFetchConfig<FiltersType = Record<any, any>> = {
  initialPage?: number;
  initialSearch?: string;
  initialPageSize?: number;
  initialSort?: SortState<string>;
  initialFilters?: FiltersType;

  resultKey?: string;
  settingsKey?: string;
  withQueryParams?: boolean;
  additionalParams?: Record<string, string>;
  deps?: any[];

  skip?: boolean;
  loadMore?: boolean;
};

export type WithPaginationResult<T> = {
  total?: number;
  totalElements?: number;
  content?: T[];
};

export type ListParamsType<FiltersType = Record<any, any>> = {
  search?: string;
  sort?: SortState<string>;
  filters?: FiltersType;
  pagination: Pagination;

  additionalParams?: Record<string, string>;
};

export type ListFetchRequestType<T, FiltersType = Record<any, any>> = (
  params: ListParamsType<FiltersType>,
  cancelToken?: AbortSignal
) => Promise<WithPaginationResult<T>> | WithPaginationResult<T>;

export type UseListFetchResult<T, FiltersType = Record<any, any>> = {
  nodes: T[];
  loading: boolean;
  initializing: boolean;
  reloading: boolean;
  error: Error | null;
  refresh: () => void;
  rawData: any;
  search: string;

  sort?: SortState<string>;
  filters?: FiltersType;

  setSort: (sort: string) => void;
  setSearch: (search: string) => void;

  setFilters: (filters: FiltersType) => void;
} & UsePaginatedResult<T>;

const getNodes = <T>(data: any, key: string): T[] => {
  return key ? (data as any)?.[key] : (data?.content ?? []);
};

const useListSort = <FiltersType>(config?: UseListFetchConfig<FiltersType>) => {
  const [urlSort, setUrlSort] = useUrlParam({
    name: 'sort',
    enable: !!config?.withQueryParams,
    defaultValue: config?.initialSort ?? undefined,
  });
  const { sort, setSort: toggleSort } = useSort({
    defaultValue: urlSort,
    useUrl: false,
  });

  const handleChange = useCallback(
    (newSort: string) => {
      toggleSort(newSort);
    },
    [toggleSort]
  );

  useUpdateEffect(
    () => {
      setUrlSort(sort.name ? sort : undefined);
    },
    [sort],
    true
  );

  return [sort, handleChange] as const;
};

export const useListFetch = <T, FiltersType = Record<any, any>>(
  request: ListFetchRequestType<T, FiltersType>,
  config?: UseListFetchConfig<FiltersType>
): UseListFetchResult<T, FiltersType> => {
  const { pagination, setPage, ...pagingProps } = usePaginated<T>({
    ...config,
    initialPageSize: config?.initialPageSize || DEFAULT_PAGE_SIZE,
  });

  const loadMoreRef = useRef(false);
  const [sort, setSort] = useListSort(config);
  const [localSearch, setSearch] = useState(config?.initialSearch ?? '');
  const search = useDebouncedValue(localSearch, 500);
  const [filters, setFilters] = useUrlParam<FiltersType>({
    name: 'filters',
    enable: !!config?.withQueryParams,
    defaultValue: (config?.initialFilters ?? {}) as FiltersType,
  });
  const setNonEmptyFilters = useCallback<typeof setFilters>(
    filters => {
      const nonEmpty = Object.entries(filters || {}).filter(
        ([_k, val]) => val !== ''
      );
      if (!nonEmpty.length) return setFilters(undefined);

      setFilters(Object.fromEntries(nonEmpty) as FiltersType);
    },
    [setFilters]
  );
  const loadMore = config?.loadMore;
  const resultKey = config?.resultKey || '';

  const params: ListParamsType<FiltersType> = {
    sort,
    search,
    filters,
    pagination: pagination,
    additionalParams: config?.additionalParams,
  };

  const { data, refresh, ...rest } = useFetch(
    token => request?.(params, token),
    [
      pagination.page,
      pagination.perPage,
      search,
      sort,
      filters,
      loadMore,
      config?.additionalParams,
      ...(config?.deps ?? []),
    ],
    {
      delay: 250,
      skip: config?.skip,
      transformData: (newData, old): any => {
        if (!loadMore || !loadMoreRef.current) {
          return newData || null;
        }

        loadMoreRef.current = false;

        const newNodes = getNodes<T>(newData, resultKey);
        const oldNodes = getNodes<T>(old, resultKey);

        return {
          ...old,
          nodes: [...oldNodes, ...newNodes],
        };
      },
    }
  );

  const handleSetSearch: UseListFetchResult<T>['setSearch'] = (...params) => {
    setPage(1);
    setSearch(...params);
  };

  const handleSetSort: UseListFetchResult<T>['setSort'] = (...params) => {
    setPage(1);
    setSort(...params);
  };

  const handleSetFilters: UseListFetchResult<T, FiltersType>['setFilters'] = (
    ...params
  ) => {
    setPage(1);
    setNonEmptyFilters(...params);
  };
  const handleRefresh = useCallback(
    () => {
      if (!loadMore) return refresh?.();

      setPage(1);
      refresh?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadMore, refresh]
  );

  const totalItems = data?.total ?? data?.totalElements ?? 0;

  return {
    ...pagingProps,
    nodes: getNodes<T>(data, resultKey),

    setPage,
    setSort: handleSetSort,
    setSearch: handleSetSearch,
    setFilters: handleSetFilters,

    rawData: data,
    sort,
    filters,
    search: localSearch,
    pagination: { ...pagination, totalItems },
    refresh: handleRefresh,

    ...rest,
  };
};
